import { PDFDocument } from 'pdf-lib';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

/**
 * Returns the number of pages in a PDF file.
 * @param {File} file - The PDF file
 * @returns {Promise<number>} - The page count
 */
export async function getPdfPageCount(file) {
  const arrayBuffer = await file.arrayBuffer();
  const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
  return pdfDoc.numPages;
}

/**
 * Compresses a PDF file by rendering pages as JPEG images and rebuilding the PDF.
 * If the file is already under maxSizeMB, returns it as-is.
 * @param {File} file - The PDF file to compress
 * @param {number} maxSizeMB - Maximum file size in MB before compression kicks in (default 10)
 * @returns {Promise<File>} - The original or compressed file
 */
export async function compressPdf(file, maxSizeMB = 10) {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size <= maxSizeBytes) {
    return file;
  }

  try {
    const arrayBuffer = await file.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    const numPages = pdfDoc.numPages;

    const newPdf = await PDFDocument.create();

    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdfDoc.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });

      // Render at 1200px width
      const targetWidth = 1200;
      const scale = targetWidth / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = scaledViewport.width;
      canvas.height = scaledViewport.height;
      const context = canvas.getContext('2d');

      await page.render({ canvasContext: context, viewport: scaledViewport }).promise;

      // Export as JPEG blob
      const jpegBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.6));
      const jpegBytes = new Uint8Array(await jpegBlob.arrayBuffer());

      // Embed into new PDF
      const jpegImage = await newPdf.embedJpg(jpegBytes);
      const newPage = newPdf.addPage([scaledViewport.width, scaledViewport.height]);
      newPage.drawImage(jpegImage, {
        x: 0,
        y: 0,
        width: scaledViewport.width,
        height: scaledViewport.height,
      });
    }

    const pdfBytes = await newPdf.save();
    return new File([pdfBytes], file.name, { type: 'application/pdf' });
  } catch (error) {
    console.error('PDF compression failed, using original file:', error);
    return file;
  }
}

/**
 * Converts an image blob to a PDF blob
 * @param {Blob} imageBlob - The image blob to convert
 * @param {string} fileName - The original file name for logging
 * @returns {Promise<Blob>} - A PDF blob containing the image
 */
export async function convertImageToPdf(imageBlob, fileName = 'image') {
  try {
    // Create a new PDF document
    const pdfDoc = await PDFDocument.create();

    // Read the image blob as array buffer
    const imageBytes = await imageBlob.arrayBuffer();

    // Determine image type from blob type or file extension
    const imageType = imageBlob.type || '';
    let image;

    if (imageType.includes('png') || fileName.toLowerCase().endsWith('.png')) {
      image = await pdfDoc.embedPng(imageBytes);
    } else if (imageType.includes('jpeg') || imageType.includes('jpg') ||
               fileName.toLowerCase().endsWith('.jpg') || fileName.toLowerCase().endsWith('.jpeg')) {
      image = await pdfDoc.embedJpg(imageBytes);
    } else {
      // Default to JPEG for unknown types
      image = await pdfDoc.embedJpg(imageBytes);
    }

    // Get image dimensions
    const { width, height } = image.scale(1);

    // Create a page that fits the image
    // Scale down if image is too large (max 8.5" x 11" at 72 DPI = 612 x 792)
    let pageWidth = width;
    let pageHeight = height;
    const maxWidth = 612;
    const maxHeight = 792;

    if (width > maxWidth || height > maxHeight) {
      const scale = Math.min(maxWidth / width, maxHeight / height);
      pageWidth = width * scale;
      pageHeight = height * scale;
    }

    const page = pdfDoc.addPage([pageWidth, pageHeight]);

    // Draw the image on the page
    page.drawImage(image, {
      x: 0,
      y: 0,
      width: pageWidth,
      height: pageHeight,
    });

    // Serialize the PDF to bytes
    const pdfBytes = await pdfDoc.save();

    // Create a blob with the correct PDF MIME type
    return new Blob([pdfBytes], { type: 'application/pdf' });

  } catch (error) {
    console.error(`Failed to convert image to PDF: ${fileName}`, error);
    throw new Error(`Failed to convert ${fileName} to PDF: ${error.message}`);
  }
}
